import { Reveal } from "@components/ui";

const doItems = [
  "Turn off the main gas valve (if accessible)",
  "Open all windows and doors for ventilation",
  "Evacuate the area immediately",
  "Call 800-GAZ (42747) from a safe location",
  "Inform building security or management",
];

const dontItems = [
  "Turn on/off electrical switches",
  "Use lighters, candles, matches, or flames",
  "Use mobile phones inside the property",
  "Attempt to restart the gas system",
];

const responseTimes = [
  {
    region: "Dubai",
    time: "60 min",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 md:w-10 md:h-10">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    region: "Sharjah / Ajman",
    time: "90 min",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 md:w-10 md:h-10">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    region: "Abu Dhabi",
    time: "120 min",
    label: "where applicable",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 md:w-10 md:h-10">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export function SmellGasSection() {
  return (
    <section className="w-full bg-[#f5f5f7] overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-12 md:gap-16 lg:gap-20">
        {/* Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#ff2600]/10 text-[#ff2600] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Emergency Protocol
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
              If You Smell Gas{" "}
              <span className="text-[#ff2600]">Act Immediately</span>
            </h2>
            <p className="text-[14px] md:text-base lg:text-[22px] tracking-[-0.04em] text-[#62636c] max-w-[974px]">
              Gas leaks can be dangerous. Follow these critical steps to ensure
              your safety and the safety of those around you.
            </p>
          </div>
        </Reveal>

        {/* Do / Don't Cards */}
        <Reveal direction="up">
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Do Card */}
            <div className="rounded-[20px] bg-white border-2 border-[#269d0e]/20 p-6 md:p-8 lg:p-10 flex flex-col gap-6 hover:shadow-[0_8px_32px_rgba(38,157,14,0.1)] transition-shadow duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#269d0e] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
                    <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="text-[24px] md:text-[32px] lg:text-[36px] font-bold tracking-[-0.04em] text-[#269d0e]">
                  Do This
                </h3>
              </div>
              <div className="flex flex-col gap-3 md:gap-4">
                {doItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 md:p-3.5 rounded-[12px] bg-[#269d0e]/[0.04] hover:bg-[#269d0e]/[0.08] transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#269d0e]/15 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[11px] font-bold text-[#269d0e]">{index + 1}</span>
                    </div>
                    <p className="text-[14px] md:text-[17px] lg:text-[19px] tracking-[-0.03em] text-[#1e1f24] leading-[1.4]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Don't Card */}
            <div className="rounded-[20px] bg-white border-2 border-[#ff2600]/20 p-6 md:p-8 lg:p-10 flex flex-col gap-6 hover:shadow-[0_8px_32px_rgba(255,38,0,0.1)] transition-shadow duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#ff2600] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
                    <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="text-[24px] md:text-[32px] lg:text-[36px] font-bold tracking-[-0.04em] text-[#ff2600]">
                  Never Do
                </h3>
              </div>
              <div className="flex flex-col gap-3 md:gap-4">
                {dontItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 md:p-3.5 rounded-[12px] bg-[#ff2600]/[0.04] hover:bg-[#ff2600]/[0.08] transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#ff2600]/15 flex items-center justify-center shrink-0 mt-0.5">
                      <svg viewBox="0 0 12 12" className="w-3 h-3">
                        <path d="M9 3L3 9M3 3l6 6" stroke="#ff2600" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className="text-[14px] md:text-[17px] lg:text-[19px] tracking-[-0.03em] text-[#1e1f24] leading-[1.4]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Emergency Response Time */}
        <Reveal direction="up">
          <div className="w-full flex flex-col items-center gap-8">
            <h3 className="text-[22px] md:text-[28px] lg:text-[36px] font-semibold tracking-[-0.04em] text-[#122d63] text-center">
              Emergency Response Time
            </h3>
            <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {responseTimes.map((item, index) => (
                <div
                  key={index}
                  className="group rounded-[20px] bg-white p-6 md:p-7 lg:p-8 flex flex-col items-center gap-4 text-center hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] hover:bg-[#122d63] transition-all duration-300"
                >
                  <div className="text-[#122d63] group-hover:text-white/70 transition-colors">
                    {item.icon}
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[36px] md:text-[42px] lg:text-[48px] font-bold tracking-[-0.04em] text-[#122d63] group-hover:text-white transition-colors leading-none">
                      {item.time}
                    </span>
                    <h4 className="text-[16px] md:text-[18px] lg:text-[20px] font-semibold tracking-[-0.03em] text-[#1e1f24] group-hover:text-white/90 transition-colors">
                      {item.region}
                    </h4>
                    {item.label && (
                      <span className="text-[12px] md:text-[13px] text-[#62636c] group-hover:text-white/50 transition-colors">
                        ({item.label})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
